import {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  loadChamadaExistente,
  loadPacoteAtivo,
  obterOuCriarAula,
  pagarAulaAvulsa,
  registrarPresenca,
} from "../turmas.service";

import type {
  FormaPagamentoAula,
  PacoteAulas,
} from "../turmas.service";

import type {
  AlunoDaTurma,
  Turma,
} from "../types";

import "./ChamadaTurma.css";


type ChamadaTurmaProps = {
  turma: Turma;

  alunos: AlunoDaTurma[];

  canManage: boolean;

  externalDate:
    string | null;

    refreshKey: number;

    onConfirmSuccess:
  () => void;
};


type AlunoChamada = {
  alunoId: string;

  presente:
    boolean | null;

  tipo:
    "pacote" |
    "avulsa";

  valorAvulsa: string;

  pacote:
    PacoteAulas | null;

    presencaId:
        string | null;

    pago: boolean;

    formaPagamento:
        FormaPagamentoAula | null;

    pagoEm:
        string | null;

  loadingPacote: boolean;
};


function todayISO() {
  const now =
    new Date();

  const year =
    now.getFullYear();

  const month =
    String(
      now.getMonth() + 1,
    ).padStart(
      2,
      "0",
    );

  const day =
    String(
      now.getDate(),
    ).padStart(
      2,
      "0",
    );

  return `${year}-${month}-${day}`;
}
const DAY_MAP: Record<
  number,
  string
> = {
  0: "Dom",
  1: "Seg",
  2: "Ter",
  3: "Qua",
  4: "Qui",
  5: "Sex",
  6: "Sáb",
};

function dateToISO(
  date: Date,
) {
  const year =
    date.getFullYear();

  const month =
    String(
      date.getMonth() + 1,
    ).padStart(
      2,
      "0",
    );

  const day =
    String(
      date.getDate(),
    ).padStart(
      2,
      "0",
    );

  return `${year}-${month}-${day}`;
}

function getNextClassDate(
  dias: string[],
) {
  const today =
    new Date();

  for (
    let offset = 0;
    offset < 7;
    offset++
  ) {
    const candidate =
      new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate() +
          offset,
      );

    const diaSemana =
      DAY_MAP[
        candidate.getDay()
      ];

    if (
      dias.includes(
        diaSemana,
      )
    ) {
      return dateToISO(
        candidate,
      );
    }
  }

  return todayISO();
}


function formatDateBR(
  value: string,
) {
  const [
    year,
    month,
    day,
  ] =
    value.split("-");

  return `${day}/${month}/${year}`;
}


export default function ChamadaTurma({
  turma,
  alunos,
  canManage,
  externalDate,
  refreshKey,
  onConfirmSuccess,
}: ChamadaTurmaProps) {
  const [
  date,
  setDate,
] =
  useState(() =>
    getNextClassDate(
      turma.dias,
    ),
  );
  useEffect(() => {
  if (!externalDate) {
    return;
  }

  setDate(
    externalDate,
  );
}, [
  externalDate,
]);

  const [
    chamada,
    setChamada,
  ] =
    useState<
      Record<
        string,
        AlunoChamada
      >
    >({});


  const [
    loading,
    setLoading,
  ] =
    useState(false);

    const [
  chamadaExistente,
  setChamadaExistente,
] =
  useState(false);


  const [
    error,
    setError,
  ] =
    useState("");

    const [
  saving,
  setSaving,
] =
  useState(false);


const [
  success,
  setSuccess,
] =
  useState("");

  const [
  paymentAlunoId,
  setPaymentAlunoId,
] =
  useState<string | null>(
    null,
  );


const [
  receivingAlunoId,
  setReceivingAlunoId,
] =
  useState<string | null>(
    null,
  );

  useEffect(() => {
  let active =
    true;


  async function loadChamada() {
    try {
      setLoading(
        true,
      );

      setError("");

      setSuccess("");

      setPaymentAlunoId(
        null,
      );


      /*
       * =====================================================
       * 1. CARREGA OS PACOTES ATUAIS
       * =====================================================
       */

      const pacotes =
        await Promise.all(
          alunos.map(
            async (
              item,
            ) => {
              const pacote =
                await loadPacoteAtivo(
                  item.aluno.id,
                  turma.id,
                );


              return {
                alunoId:
                  item.aluno.id,

                pacote,
              };
            },
          ),
        );


      if (!active) {
        return;
      }


      /*
       * =====================================================
       * 2. PROCURA CHAMADA JÁ REGISTRADA NESTA DATA
       * =====================================================
       */

      const existente =
        await loadChamadaExistente(
          turma.id,
          date,
          turma.hora,
        );

        if (active) {
  setChamadaExistente(
    Boolean(
      existente &&
      existente.presencas.length > 0,
    ),
  );
}


      if (!active) {
        return;
      }


      /*
       * =====================================================
       * 3. CRIA MAPAS PARA FACILITAR A BUSCA
       * =====================================================
       */

      const pacotePorAluno =
        new Map(
          pacotes.map(
            (item) => [
              item.alunoId,
              item.pacote,
            ],
          ),
        );


      const presencaPorAluno =
        new Map(
          (
            existente?.presencas ??
            []
          ).map(
            (presenca) => [
              presenca.aluno_id,
              presenca,
            ],
          ),
        );


      /*
       * =====================================================
       * 4. MONTA O ESTADO DA CHAMADA
       * =====================================================
       */

      const entries =
        alunos.map(
          (item) => {
            const alunoId =
              item.aluno.id;

            const pacote =
              pacotePorAluno.get(
                alunoId,
              ) ?? null;

            const presenca =
              presencaPorAluno.get(
                alunoId,
              );


            return [
              alunoId,
              {
                alunoId,

                presente:
                  presenca
                    ? presenca.presente
                    : null,

                tipo:
                  presenca
                    ? presenca.tipo_pagamento
                    : pacote
                      ? "pacote"
                      : "avulsa",

                valorAvulsa:
                  presenca &&
                  presenca.tipo_pagamento ===
                    "avulsa"
                    ? String(
                        presenca.valor_avulso,
                      ).replace(
                        ".",
                        ",",
                      )
                    : "",

                pacote,

                presencaId:
                  presenca?.id ??
                  null,

                pago:
                  presenca?.pago ??
                  false,

                formaPagamento:
                  presenca?.forma_pagamento
                    ? (
                        presenca.forma_pagamento as
                          FormaPagamentoAula
                      )
                    : null,

                pagoEm:
                  presenca?.pago_em ??
                  null,

                loadingPacote:
                  false,
              } satisfies AlunoChamada,
            ] as const;
          },
        );


      if (!active) {
        return;
      }


      setChamada(
        Object.fromEntries(
          entries,
        ),
      );
    } catch (err) {
      console.error(
        err,
      );


      if (active) {
        setError(
          err instanceof Error
            ? err.message
            : "Não foi possível carregar os dados da chamada.",
        );
      }
    } finally {
      if (active) {
        setLoading(
          false,
        );
      }
    }
  }


  loadChamada();


  return () => {
    active =
      false;
  };
}, [
  alunos,
  turma.id,
  turma.hora,
  date,
  refreshKey,
]);

  const resumo =
    useMemo(
      () => {
        const values =
          Object.values(
            chamada,
          );


        return {
          presentes:
            values.filter(
              (item) =>
                item.presente ===
                true,
            ).length,

          faltas:
            values.filter(
              (item) =>
                item.presente ===
                false,
            ).length,

          pendentes:
            values.filter(
              (item) =>
                item.presente ===
                null,
            ).length,
        };
      },
      [
        chamada,
      ],
    );


  function updateAluno(
    alunoId: string,
    patch:
      Partial<AlunoChamada>,
  ) {
    setChamada(
      (current) => ({
        ...current,

        [alunoId]: {
          ...current[
            alunoId
          ],

          ...patch,
        },
      }),
    );
  }
  async function handleConfirmChamada() {
  const alunosChamada =
    Object.values(
      chamada,
    );


  if (
    alunosChamada.some(
      (item) =>
        item.presente ===
        null,
    )
  ) {
    setError(
      "Marque Presente ou Faltou para todos os alunos.",
    );

    return;
  }


  /*
   * =====================================================
   * VALIDA AULAS AVULSAS
   * =====================================================
   */

  for (
    const item
    of alunosChamada
  ) {
    if (
      item.presente ===
        true &&
      item.tipo ===
        "avulsa"
    ) {
      const value =
        Number(
          item.valorAvulsa
            .replace(
              ",",
              ".",
            ),
        );


      if (
        item.valorAvulsa.trim() ===
          "" ||
        Number.isNaN(
          value,
        ) ||
        value < 0
      ) {
        setError(
          "Informe um valor válido para todas as aulas avulsas.",
        );

        return;
      }
    }
  }


  try {
    setSaving(
      true,
    );

    setError("");

    setSuccess("");


    /*
     * =====================================================
     * 1. OBTÉM / CRIA A AULA
     * =====================================================
     */

    const aula =
      await obterOuCriarAula(
        turma.id,
        date,
        turma.hora,
      );


    /*
     * =====================================================
     * 2. REGISTRA PRESENÇAS
     * =====================================================
     *
     * Guardamos todos os retornos
     * antes de atualizar o state.
     * =====================================================
     */

    const presencasResult =
      await Promise.all(
        alunosChamada.map(
          async (
            item,
          ) => {
            const valor =
              item.tipo ===
                "avulsa"
                ? Number(
                    item.valorAvulsa
                      .replace(
                        ",",
                        ".",
                      ) ||
                      0,
                  )
                : 0;


            const presenca =
              await registrarPresenca(
                aula.id,

                item.alunoId,

                item.presente ===
                  true,

                item.tipo,

                valor,

                item.tipo ===
                    "pacote" &&
                  item.presente ===
                    true
                  ? item.pacote
                      ?.id ??
                    null
                  : null,
              );


            return {
              alunoId:
                item.alunoId,

              presenca,
            };
          },
        ),
      );


    /*
     * =====================================================
     * 3. RECARREGA PACOTES
     * =====================================================
     */

    const pacotesResult =
      await Promise.all(
        alunos.map(
          async (
            item,
          ) => {
            const pacote =
              await loadPacoteAtivo(
                item.aluno.id,
                turma.id,
              );


            return {
              alunoId:
                item.aluno.id,

              pacote,
            };
          },
        ),
      );


    /*
     * =====================================================
     * 4. MONTA MAPAS
     * =====================================================
     */

    const presencaPorAluno =
      new Map(
        presencasResult.map(
          (item) => [
            item.alunoId,
            item.presenca,
          ],
        ),
      );


    const pacotePorAluno =
      new Map(
        pacotesResult.map(
          (item) => [
            item.alunoId,
            item.pacote,
          ],
        ),
      );


    /*
     * =====================================================
     * 5. ATUALIZA CHAMADA UMA ÚNICA VEZ
     * =====================================================
     */

    setChamada(
      (current) => {
        const next = {
          ...current,
        };


        alunos.forEach(
          (item) => {
            const alunoId =
              item.aluno.id;

            const currentAluno =
              current[
                alunoId
              ];


            if (!currentAluno) {
              return;
            }


            const presenca =
              presencaPorAluno.get(
                alunoId,
              );


            const pacote =
              pacotePorAluno.get(
                alunoId,
              ) ??
              null;


            next[
              alunoId
            ] = {
              ...currentAluno,

              pacote,

              presencaId:
                presenca?.id ??
                currentAluno.presencaId,

              pago:
                presenca
                  ? presenca.pago
                  : currentAluno.pago,

              formaPagamento:
                presenca
                  ? (
                      presenca.forma_pagamento as
                        FormaPagamentoAula | null
                    )
                  : currentAluno.formaPagamento,

              pagoEm:
                presenca?.pago_em ??
                currentAluno.pagoEm,

              loadingPacote:
                false,
            };
          },
        );


        return next;
      },
    );


    setSuccess(
  "Chamada registrada com sucesso.",
);

onConfirmSuccess();
  } catch (err) {
    console.error(
      err,
    );


    let message =
  "Não foi possível confirmar a chamada.";

if (
  typeof err === "object" &&
  err !== null &&
  "message" in err
) {
  const errorMessage =
    String(
      (
        err as {
          message?: string;
        }
      ).message ?? "",
    ).trim();

  if (errorMessage) {
    message =
      errorMessage;
  }
}

setError(
  message,
);
  } finally {
    setSaving(
      false,
    );
  }
}

async function handleReceiveAvulsa(
  alunoId: string,
  formaPagamento:
    FormaPagamentoAula,
) {
  const state =
    chamada[
      alunoId
    ];

  if (
    !state ||
    state.tipo !==
      "avulsa" ||
    state.presente !==
      true ||
    state.pago
  ) {
    return;
  }

  try {
    setReceivingAlunoId(
      alunoId,
    );

    setError("");

    setSuccess("");


    /*
     * =====================================================
     * 1. GARANTE QUE A AULA EXISTE
     * =====================================================
     */

    const aula =
      await obterOuCriarAula(
        turma.id,
        date,
        turma.hora,
      );


    /*
     * =====================================================
     * 2. SALVA A PRESENÇA COMO AULA AVULSA
     * =====================================================
     *
     * Isso é importante caso o usuário tenha alterado
     * Pacote -> Avulsa e clique diretamente em Receber
     * antes de confirmar novamente a chamada.
     */

    const valor =
      Number(
        state.valorAvulsa
          .replace(
            ",",
            ".",
          ) || 0,
      );


    if (
      Number.isNaN(
        valor,
      ) ||
      valor <= 0
    ) {
      throw new Error(
        "Informe um valor válido para a aula avulsa.",
      );
    }


    const presenca =
      await registrarPresenca(
        aula.id,
        alunoId,
        true,
        "avulsa",
        valor,
        null,
      );


    /*
     * =====================================================
     * 3. RECEBE O PAGAMENTO
     * =====================================================
     *
     * Somente depois de o banco confirmar que esta
     * presença realmente é uma aula avulsa.
     */

    const result =
      await pagarAulaAvulsa(
        presenca.id,
        formaPagamento,
      );


    /*
     * =====================================================
     * 4. ATUALIZA A TELA
     * =====================================================
     */

    setChamada(
      (current) => ({
        ...current,

        [alunoId]: {
          ...current[
            alunoId
          ],

          presencaId:
            presenca.id,

          tipo:
            "avulsa",

          presente:
            true,

          pago:
            true,

          formaPagamento:
            result.forma_pagamento,

          pagoEm:
            result.pago_em,
        },
      }),
    );


    setPaymentAlunoId(
      null,
    );


    setSuccess(
      "Aula avulsa recebida com sucesso.",
    );
  } catch (err) {
    console.error(
      "Erro ao receber aula avulsa:",
      err,
    );


    let message =
      "Não foi possível receber a aula avulsa.";


    if (
      typeof err === "object" &&
      err !== null &&
      "message" in err
    ) {
      const errorMessage =
        String(
          (
            err as {
              message?: string;
            }
          ).message ??
          "",
        ).trim();


      if (errorMessage) {
        message =
          errorMessage;
      }
    }


    setError(
      message,
    );
  } finally {
    setReceivingAlunoId(
      null,
    );
  }
}


  return (
    <section className="chamada-turma">
      <div className="chamada-header">
        <div>
          <span className="turmas-eyebrow">
            AULA
          </span>

          <h3>
            Chamada
          </h3>

          <p>
            {formatDateBR(
              date,
            )}
            {" • "}
            {turma.hora}
          </p>
        </div>


        <label className="chamada-date">
          Data

          <input
            type="date"
            value={
              date
            }
            onChange={(
              event,
            ) =>
              setDate(
                event.target
                  .value,
              )
            }
          />
        </label>
      </div>

{chamadaExistente && (
  <div className="chamada-existing-notice">
    <strong>
      ✓ Chamada registrada
    </strong>

    <span>
      Esta data já possui uma chamada salva.
      Você pode fazer alterações e confirmar
      novamente, se necessário.
    </span>
  </div>
)}

<div className="chamada-resumo">        <div>
          <span>
            Presentes
          </span>

          <strong>
            {
              resumo.presentes
            }
          </strong>
        </div>

        <div>
          <span>
            Faltas
          </span>

          <strong>
            {
              resumo.faltas
            }
          </strong>
        </div>

        <div>
          <span>
            Pendentes
          </span>

          <strong>
            {
              resumo.pendentes
            }
          </strong>
        </div>
      </div>


      {loading ? (
        <div className="turmas-empty">
          Carregando chamada...
        </div>
      ) : alunos.length ===
        0 ? (
        <div className="turmas-empty">
          Nenhum aluno na turma.
        </div>
      ) : (
        <div className="chamada-list">
          {alunos.map(
            (item) => {
              const state =
                chamada[
                  item.aluno.id
                ];


              if (!state) {
                return null;
              }


              const restantes =
                state.pacote
                  ? Math.max(
                      state.pacote
                        .quantidade_aulas -
                        state.pacote
                          .aulas_usadas,
                      0,
                    )
                  : 0;


              return (
                <div
                  key={
                    item.aluno.id
                  }
                  className="chamada-aluno"
                >
                  <div className="chamada-aluno-top">
                    <div>
                      <strong>
                        {
                          item.aluno
                            .nome
                        }
                      </strong>

                      <span>
                        Nível{" "}
                        {
                          item.aluno
                            .nivel
                        }
                      </span>
                    </div>


                    <div className="chamada-presenca-buttons">
                      <button
                        type="button"
                        className={
                          state.presente ===
                          true
                            ? "active present"
                            : ""
                        }
                        onClick={() =>
                          updateAluno(
                            item.aluno
                              .id,
                            {
                              presente:
                                true,
                            },
                          )
                        }
                        disabled={
  !canManage ||
  state.pago
}
                      >
                        Presente
                      </button>

                      <button
                        type="button"
                        className={
                          state.presente ===
                          false
                            ? "active absent"
                            : ""
                        }
                        onClick={() =>
                          updateAluno(
                            item.aluno
                              .id,
                            {
                              presente:
                                false,
                            },
                          )
                        }
                        disabled={
  !canManage ||
  state.pago
}
                      >
                        Faltou
                      </button>
                    </div>
                  </div>


                  <div className="chamada-payment-mode">
                    <label>
                      <input
                        type="radio"
                        name={`tipo-${item.aluno.id}`}
                        checked={
                          state.tipo ===
                          "pacote"
                        }
                        disabled={
  !canManage ||
  !state.pacote ||
  state.pago
}
                        onChange={() =>
                          updateAluno(
                            item.aluno
                              .id,
                            {
                              tipo:
                                "pacote",
                            },
                          )
                        }
                      />

                      Pacote
                    </label>


                    <label>
                      <input
                        type="radio"
                        name={`tipo-${item.aluno.id}`}
                        checked={
                          state.tipo ===
                          "avulsa"
                        }
                        disabled={
  !canManage ||
  state.pago
}
                        onChange={() =>
                          updateAluno(
                            item.aluno
                              .id,
                            {
                              tipo:
                                "avulsa",
                            },
                          )
                        }
                      />

                      Avulsa
                    </label>
                  </div>


                  {state.tipo ===
                  "pacote" ? (
                    <div className="chamada-package-info">
                      {state.pacote ? (
                        <>
                          <span>
                            Pacote:{" "}
                            {
                              state.pacote
                                .quantidade_aulas
                            }{" "}
                            aulas
                          </span>

                          <span>
                            Usadas:{" "}
                            {
                              state.pacote
                                .aulas_usadas
                            }
                          </span>

                          <strong>
                            Restantes:{" "}
                            {
                              restantes
                            }
                          </strong>
                        </>
                      ) : (
                        <span>
                          Nenhum pacote ativo.
                        </span>
                      )}
                    </div>
                  ) : (
                    <label className="chamada-avulsa-value">
                      Valor da aula avulsa

                      <input
                        type="text"
                        inputMode="decimal"
                        value={
                          state.valorAvulsa
                        }
                        disabled={
  !canManage ||
  state.pago
}
                        onChange={(
                          event,
                        ) =>
                          updateAluno(
                            item.aluno
                              .id,
                            {
                              valorAvulsa:
                                event
                                  .target
                                  .value,
                            },
                          )
                        }
                        placeholder="Ex.: 40,00"
                      />
                    </label>
                  )}
                  {state.presencaId &&
    state.presente === true && 
     state.tipo === "avulsa" && (
  <div className="chamada-avulsa-payment">
    <div className="chamada-avulsa-payment-header">
      <div>
        <span>
          Pagamento
        </span>

        <strong>
          {state.pago
            ? "PAGO"
            : "PENDENTE"}
        </strong>
      </div>

      {!state.pago && (
        <button
          type="button"
          className="chamada-avulsa-receive"
          onClick={() =>
            setPaymentAlunoId(
              paymentAlunoId ===
                item.aluno.id
                ? null
                : item.aluno.id,
            )
          }
          disabled={
            receivingAlunoId ===
            item.aluno.id
          }
        >
          {receivingAlunoId ===
          item.aluno.id
            ? "Recebendo..."
            : `Receber R$ ${Number(
                state.valorAvulsa
                  .replace(
                    ",",
                    ".",
                  ) || 0,
              ).toFixed(2)
                .replace(
                  ".",
                  ",",
                )}`}
        </button>
      )}
    </div>


    {state.pago && (
      <div className="chamada-avulsa-paid-info">
        Forma:{" "}
        {state.formaPagamento ||
          "Não informada"}
      </div>
    )}


    {paymentAlunoId ===
      item.aluno.id &&
      !state.pago && (
      <div className="chamada-avulsa-payment-options">
        <button
          type="button"
          onClick={() =>
            handleReceiveAvulsa(
              item.aluno.id,
              "pix",
            )
          }
        >
          Pix
        </button>

        <button
          type="button"
          onClick={() =>
            handleReceiveAvulsa(
              item.aluno.id,
              "dinheiro",
            )
          }
        >
          Dinheiro
        </button>

        <button
          type="button"
          onClick={() =>
            handleReceiveAvulsa(
              item.aluno.id,
              "debito",
            )
          }
        >
          Débito
        </button>

        <button
          type="button"
          onClick={() =>
            handleReceiveAvulsa(
              item.aluno.id,
              "credito",
            )
          }
        >
          Crédito
        </button>
      </div>
    )}
  </div>
)}
                </div>
              );
            },
          )}
        </div>
      )}


      {error && (
        <div className="turmas-form-error">
          {error}
        </div>
      )}

      {success && (
  <div className="chamada-success">
    {success}
  </div>
)}


      {canManage &&
        alunos.length >
          0 && (
        <button
  type="button"
  className="turmas-primary-button chamada-confirm"
  onClick={
    handleConfirmChamada
  }
  disabled={
    loading ||
    saving
  }
>
  {saving
    ? "Salvando chamada..."
    : "Confirmar chamada"}
</button>
      )}
    </section>
  );
}